import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useBlocker, useNavigate } from "@tanstack/react-router";
import { useRecords } from "./useRecords";
import { RecordsView } from "./RecordsView";
import { RecordDetailDialog } from "./RecordDetailDialog";
import { CreateRecordDialog } from "./CreateRecordDialog";
import { fetchBootstrap, createRecord, updateRecord } from "./api";
import { loadFilters, saveFilters } from "../../lib/storage";
import type { FilterState } from "./filters";
import type { NormalizedRecord } from "./types";
import { useRecordEvents } from "./useRecordEvents";

/**
 * RecordsPage is the single entry point mounted by the router for both
 * `/` and `/console/records`. It owns:
 *  - the bootstrap query (so loading / error / mapping warnings have one home)
 *  - filter restoration + persistence (spec §4.6)
 *  - the create / detail dialogs (spec §5.5 / §5.6)
 */
interface RecordsPageProps {
  routeRecordId: string | undefined;
}

export function RecordsPage({ routeRecordId }: RecordsPageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<FilterState>({
    keyword: "",
    category: "",
    theme: "",
    status: "",
  });
  const [hydrated, setHydrated] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createDirty, setCreateDirty] = useState(false);
  const [detailDirty, setDetailDirty] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const detailTriggerRef = useRef<HTMLButtonElement | null>(null);
  const hasUnsavedChanges = createDirty || detailDirty;
  const recordEvents = useRecordEvents();

  useBlocker({
    disabled: !hasUnsavedChanges,
    enableBeforeUnload: hasUnsavedChanges,
    shouldBlockFn: () => !window.confirm("有未保存的更改，确定要离开吗？"),
  });

  const bootstrapQuery = useQuery({
    queryKey: ["bootstrap"],
    queryFn: () => fetchBootstrap(),
    staleTime: 30_000,
  });

  // Restore filters once the bootstrap data is available so we can validate
  // category / theme values against the known option list (spec §4.6).
  useEffect(() => {
    if (!bootstrapQuery.data || hydrated) return;
    const restored = loadFilters(
      bootstrapQuery.data.known_categories ?? [],
      bootstrapQuery.data.known_themes ?? [],
      bootstrapQuery.data.record_status_options ?? [],
    );
    setFilters(restored);
    setHydrated(true);
  }, [bootstrapQuery.data, hydrated]);

  useEffect(() => {
    if (hydrated) saveFilters(filters);
  }, [filters, hydrated]);

  useEffect(() => {
    if (routeRecordId !== undefined || !detailTriggerRef.current) return;
    detailTriggerRef.current.focus();
    detailTriggerRef.current = null;
  }, [routeRecordId]);

  const recordsQuery = useRecords(filters, bootstrapQuery.data);

  async function refreshRecords() {
    setRefreshing(true);
    try {
      const data = await fetchBootstrap(true);
      queryClient.setQueryData(["bootstrap"], data);
      recordEvents.clearUpdate();
    } finally {
      setRefreshing(false);
    }
  }

  if (bootstrapQuery.isLoading) {
    return <div className="p-4 text-sm">加载中…</div>;
  }
  if (bootstrapQuery.isError) {
    return (
      <div className="p-4 text-sm">
        <p className="text-red-600">bootstrap 请求失败。</p>
        <button
          type="button"
          onClick={() => {
            bootstrapQuery.refetch();
          }}
          className="mt-2 underline"
        >
          重试
        </button>
      </div>
    );
  }

  const allRecords = bootstrapQuery.data?.records ?? [];
  const categoryOptions = orderedOptions(
    bootstrapQuery.data?.known_categories ?? [],
    bootstrapQuery.data?.feishu_category_options ?? [],
  );
  const themeOptions = (bootstrapQuery.data?.known_themes ?? []).filter((theme) => {
    if (!filters.category) return true;
    const mappedCategory = bootstrapQuery.data?.category_by_theme?.[theme];
    if (mappedCategory) return mappedCategory === filters.category;
    return allRecords.some(
      (record) => record.theme === theme && record.category === filters.category,
    );
  });
  const openRecord =
    routeRecordId === undefined
      ? null
      : allRecords.find((r) => r.recordId === routeRecordId) ?? null;

  return (
    <>
      <RecordsView
        groups={recordsQuery.grouped}
        totalCount={recordsQuery.totalCount}
        allRecordCount={allRecords.length}
        filters={filters}
        categoryOptions={categoryOptions}
        themeOptions={themeOptions}
        recordStatusOptions={recordsQuery.recordStatusOptions}
        mappingErrors={bootstrapQuery.data?.mapping_errors ?? []}
        refreshing={refreshing}
        eventsStatus={recordEvents.status}
        hasRemoteUpdate={recordEvents.hasUpdate}
        onFiltersChange={(next) => setFilters(next)}
        onRefresh={() => void refreshRecords()}
        onOpenRecord={(recordId, trigger) => {
          detailTriggerRef.current = trigger;
          navigate({ to: "/console/records/$recordId", params: { recordId } });
        }}
        onOpenCreate={() => setCreateOpen(true)}
      />
      {routeRecordId !== undefined && openRecord === null ? (
        <section className="record-not-found" aria-live="polite">
          <h2>没有找到这条记录。</h2>
          <p>记录可能已删除，或者链接中的 ID 不正确。</p>
          <Link to="/console/records">返回记录列表</Link>
        </section>
      ) : null}
      <RecordDetailDialog
        record={openRecord}
        statusOptions={recordsQuery.recordStatusOptions}
        knownThemes={bootstrapQuery.data?.known_themes ?? []}
        knownCategories={categoryOptions}
        categoryByTheme={bootstrapQuery.data?.category_by_theme ?? {}}
        mappingHasErrors={(bootstrapQuery.data?.mapping_errors ?? []).length > 0}
        onDirtyChange={setDetailDirty}
        onClose={() => {
          void navigate({ to: "/console/records", ignoreBlocker: true });
        }}
        onSubmit={async (input) => {
          if (!openRecord) return;
          await updateRecord(openRecord.recordId, input);
          await refreshRecords();
        }}
      />
      <CreateRecordDialog
        statusOptions={recordsQuery.recordStatusOptions}
        knownThemes={bootstrapQuery.data?.known_themes ?? []}
        knownCategories={categoryOptions}
        categoryByTheme={bootstrapQuery.data?.category_by_theme ?? {}}
        mappingHasErrors={(bootstrapQuery.data?.mapping_errors ?? []).length > 0}
        open={createOpen}
        onDirtyChange={setCreateDirty}
        onClose={() => setCreateOpen(false)}
        onSubmit={async (input) => {
          await createRecord(input);
          await refreshRecords();
        }}
      />
    </>
  );
}

export type { NormalizedRecord };

function orderedOptions(values: string[], preferredOrder: string[]): string[] {
  const available = new Set(values.filter(Boolean));
  const ordered = preferredOrder.filter((value) => available.delete(value));
  return [...ordered, ...Array.from(available).sort((a, b) => a.localeCompare(b, "zh-Hans-CN"))];
}
